// src/components/roads/RoadMapDrawingPanel.js
import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, Input, InputGroup, InputLeftElement,
    Icon, Spinner, Table, Thead, Tbody, Tr, Th, Td, useColorModeValue, IconButton,
    Alert, AlertIcon, AlertTitle, AlertDescription,
} from '@chakra-ui/react';
import { MdSearch, MdArrowBack, MdArrowForward, MdClose, MdOutlineMap, MdRoute, MdEditLocation, MdInfoOutline, MdCancel } from 'react-icons/md';
import { FaRoad } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// 🚀 FIXED: Added the roadService import so we hit the correct backend routes
import * as roadService from '../../services/roadService';

const MotionBox = motion(Box);
const MotionTr = motion(Tr);

// ─────────────────────────────────────────────────────────────────────────────
// DRAWING MODE BANNER — shown above the panel while user is drawing
// ─────────────────────────────────────────────────────────────────────────────
const DrawingModeBanner = memo(({ roadBeingMapped, onCancelMapping }) => {
    if (!roadBeingMapped) return null;

    return (
        <MotionBox
            initial={{ height: 0, opacity: 0, y: -10 }}
            animate={{ height: 'auto', opacity: 1, y: 0 }}
            exit={{ height: 0, opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
            <Alert
                status="info"
                borderRadius="xl"
                boxSize="full"
                boxShadow="0 4px 16px rgba(49, 130, 206, 0.15)"
                border="1px solid"
                borderColor="blue.300"
                bg="blue.50"
                py={4}
            >
                <AlertIcon as={MdEditLocation} color="blue.500" boxSize={6} />
                <Box flex="1">
                    <AlertTitle fontSize="md" color="blue.800">
                        🎯 Drawing Mode Active — {roadBeingMapped.name}
                    </AlertTitle>
                    <AlertDescription fontSize="sm" color="blue.700">
                        <b>Click</b> on the map to add waypoints along the road.{' '}
                        <b>Right-click</b> to undo the last point.{' '}
                        <b>Double-click</b> to finalize and save the path.
                        {roadBeingMapped.metadata?.road_code && (
                            <Text as="span" ml={2} fontFamily="mono" fontSize="xs" color="blue.600">
                                [{roadBeingMapped.metadata.road_code}]
                            </Text>
                        )}
                    </AlertDescription>
                </Box>
                <Button
                    size="sm"
                    colorScheme="red"
                    variant="solid"
                    leftIcon={<Icon as={MdCancel} />}
                    onClick={onCancelMapping}
                    borderRadius="lg"
                    _hover={{ transform: 'translateY(-1px)' }}
                    transition="all 0.2s"
                >
                    Cancel Drawing
                </Button>
            </Alert>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PANEL
// ─────────────────────────────────────────────────────────────────────────────
const RoadMapDrawingPanel = ({
    roadBeingMapped,
    onStartMapping,
    onCancelMapping,
}) => {
    // ── Theme Colors ──────────────────────────────────────────────────────────
    const cardBg       = useColorModeValue('white', 'gray.800');
    const borderColor  = useColorModeValue('orange.200', 'orange.700');
    const headerBg     = useColorModeValue('orange.50', 'orange.900');
    const tableHeadBg  = useColorModeValue('gray.50', 'gray.750');
    const hoverBg      = useColorModeValue('gray.50', 'gray.750');
    const mutedColor   = useColorModeValue('gray.500', 'gray.400');
    const headerIconBg    = useColorModeValue('orange.50', 'orange.900');
    const headerIconColor = useColorModeValue('orange.500', 'orange.300');

    const helperHintBg        = useColorModeValue('blue.50', 'blue.900');
    const helperHintTextColor = useColorModeValue('blue.700', 'blue.200');
    const infoBorderColor     = useColorModeValue('blue.100', 'blue.800');
    const searchBorderColor   = useColorModeValue('gray.200', 'gray.700');

    const activeRowBg = useColorModeValue('blue.50', 'blue.900');

    // ── Internal State: Data, Search, Sort, Pagination ────────────────────────
    const [roads, setRoads] = useState([]);
    const [isFetching, setIsFetching] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('name');
    const [sortDir, setSortDir] = useState('ASC');
    const limit = 10;

    // Debounce search input to prevent spamming the database
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1); // Reset to page 1 on new search
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // 🚀 FIXED: Replaced raw fetch with roadService to hit the correct /roads/list endpoint
    const fetchUnmappedRoads = useCallback(async () => {
        setIsFetching(true);
        try {
            const params = {
                limit,
                offset: (page - 1) * limit,
                sort_by: sortBy,
                sort_dir: sortDir,
                unmapped_only: 'true' // Trigger the backend filter
            };

            if (debouncedSearch) {
                params.search = debouncedSearch;
            }

            const res = await roadService.getRoadsAdvanced(params);

            if (res && res.data) {
                setRoads(res.data);
                setTotalPages(res.pagination?.total_pages || 1);
                setTotalRecords(res.pagination?.total || 0);
            }
        } catch (error) {
            console.error('Failed to fetch unmapped roads', error);
        } finally {
            setIsFetching(false);
        }
    }, [page, sortBy, sortDir, debouncedSearch]);

    // Re-fetch whenever dependencies change
    useEffect(() => {
        fetchUnmappedRoads();
    }, [fetchUnmappedRoads]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleSort = (col) => {
        if (sortBy === col) {
            setSortDir(sortDir === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(col);
            setSortDir('ASC');
        }
        setPage(1);
    };

    const renderSortIcon = (col) => {
        if (sortBy !== col) return null;
        return <Text as="span" ml={1} fontSize="xs">{sortDir === 'ASC' ? '▲' : '▼'}</Text>;
    };

    return (
        <VStack spacing={4} align="stretch" w="100%">

            {/* Drawing-mode banner — renders only while a road is being mapped */}
            <AnimatePresence>
                {roadBeingMapped && (
                    <DrawingModeBanner
                        roadBeingMapped={roadBeingMapped}
                        onCancelMapping={onCancelMapping}
                    />
                )}
            </AnimatePresence>

            {/* Main Card */}
            <MotionBox
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <Box
                    bg={cardBg}
                    borderRadius="2xl"
                    borderWidth="1px"
                    borderColor={borderColor}
                    overflow="hidden"
                    boxShadow="sm"
                    position="relative"
                >
                    {/* Top accent bar */}
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        h="3px"
                        bgGradient="linear(to-r, orange.400, red.400)"
                        borderTopRadius="2xl"
                    />

                    {/* Orange Header */}
                    <Flex
                        bg={headerBg}
                        p={4}
                        justify="space-between"
                        align="center"
                        borderBottomWidth="1px"
                        borderColor={borderColor}
                    >
                        <HStack spacing={3}>
                            <Box
                                bg={headerIconBg}
                                p={2.5}
                                borderRadius="xl"
                                boxShadow="0 4px 14px var(--chakra-colors-orange-200)"
                            >
                                <Icon as={MdOutlineMap} color={headerIconColor} boxSize={5} />
                            </Box>
                            <Box>
                                <Text fontSize="md" fontWeight="800" color="orange.800">Unmapped Roads</Text>
                                <Text fontSize="xs" color="orange.600" fontWeight="500">
                                    {totalRecords === 0
                                        ? 'All roads have spatial coordinates'
                                        : `${totalRecords} road${totalRecords === 1 ? '' : 's'} missing spatial coordinates`}
                                </Text>
                            </Box>
                        </HStack>
                        {totalRecords > 0 && (
                            <Badge colorScheme="orange" variant="solid" borderRadius="full" px={3} py={1} fontSize="xs" fontWeight="700">
                                {totalRecords} PENDING
                            </Badge>
                        )}
                    </Flex>

                    {/* Helper hint row */}
                    {totalRecords > 0 && !roadBeingMapped && (
                        <Flex
                            px={5}
                            py={2.5}
                            bg={helperHintBg}
                            borderBottomWidth="1px"
                            borderColor={infoBorderColor}
                            align="center"
                            gap={2}
                        >
                            <Icon as={MdInfoOutline} color="blue.500" boxSize={4} flexShrink={0} />
                            <Text fontSize="xs" color={helperHintTextColor}>
                                Click <b>Draw on Map</b> for any road below to trace its path on the GIS map.
                                Geometry is saved to the database immediately on completion.
                            </Text>
                        </Flex>
                    )}

                    {/* Search Bar */}
                    <Box p={4} borderBottomWidth="1px" borderColor={searchBorderColor}>
                        <InputGroup maxW="400px" size="sm">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={MdSearch} color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search by name, code, or constituency..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                borderRadius="lg"
                            />
                            {search && (
                                <InputLeftElement cursor="pointer" onClick={() => setSearch('')} ml="360px">
                                    <Icon as={MdClose} color="gray.400" />
                                </InputLeftElement>
                            )}
                        </InputGroup>
                    </Box>

                    {/* Data Table */}
                    <Box overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead bg={tableHeadBg}>
                                <Tr>
                                    <Th py={3}>ROAD CODE</Th>
                                    <Th cursor="pointer" onClick={() => handleSort('name')} _hover={{ color: 'blue.500' }} py={3}>
                                        ASSET NAME & EXTENT {renderSortIcon('name')}
                                    </Th>
                                    <Th cursor="pointer" onClick={() => handleSort('constituency')} _hover={{ color: 'blue.500' }}>
                                        CONSTITUENCY {renderSortIcon('constituency')}
                                    </Th>
                                    <Th cursor="pointer" onClick={() => handleSort('length_m')} isNumeric _hover={{ color: 'blue.500' }}>
                                        LENGTH (M) {renderSortIcon('length_m')}
                                    </Th>
                                    <Th textAlign="right">ACTION</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {isFetching ? (
                                    <Tr>
                                        <Td colSpan={5} textAlign="center" py={10}>
                                            <Spinner color="blue.500" />
                                        </Td>
                                    </Tr>
                                ) : roads.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={5} textAlign="center" py={8}>
                                            {debouncedSearch ? (
                                                <Text color="gray.500" fontWeight="600">
                                                    No unmapped roads match your search.
                                                </Text>
                                            ) : (
                                                <Flex direction="column" align="center" gap={3}>
                                                    <MotionBox
                                                        animate={{ y: [0, -6, 0] }}
                                                        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                                                    >
                                                        <Icon as={FaRoad} boxSize={12} color="green.300" />
                                                    </MotionBox>
                                                    <Text fontWeight="700" color="gray.500" fontSize="sm">
                                                        All roads are mapped 🎉
                                                    </Text>
                                                    <Text fontSize="xs" color={mutedColor} textAlign="center" maxW="320px">
                                                        Every road segment in your inventory has spatial coordinates
                                                        and is visible on the GIS network map above.
                                                    </Text>
                                                </Flex>
                                            )}
                                        </Td>
                                    </Tr>
                                ) : (
                                    <AnimatePresence>
                                        {roads.map((road) => {
                                            const isMappingThis = roadBeingMapped?.id === road.id;
                                            return (
                                                <MotionTr
                                                    key={road.id}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                    bg={isMappingThis ? activeRowBg : 'transparent'} 
                                                    _hover={{ bg: isMappingThis ? undefined : hoverBg }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <Td fontWeight="bold" fontFamily="mono" color="blue.500" fontSize="xs">
                                                        {road.metadata?.road_code || '—'}
                                                    </Td>
                                                    <Td>
                                                        <Text fontWeight="800" fontSize="xs" color={isMappingThis ? 'blue.600' : undefined}>
                                                            {road.name}
                                                        </Text>
                                                        <Text fontSize="2xs" color={mutedColor} noOfLines={1} maxW="200px">
                                                            {road.location_description || 'No location details'}
                                                        </Text>
                                                    </Td>
                                                    <Td fontSize="xs" fontWeight="500" color={mutedColor}>
                                                        {road.metadata?.constituency?.toUpperCase() || '—'}
                                                    </Td>
                                                    <Td isNumeric fontFamily="mono" fontSize="xs">
                                                        {road.metadata?.length_m ? Number(road.metadata.length_m).toFixed(0) : '—'}
                                                    </Td>
                                                    <Td textAlign="right">
                                                        {isMappingThis ? (
                                                            <Button size="xs" colorScheme="red" variant="outline" onClick={onCancelMapping}>
                                                                Cancel Drawing
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                size="xs"
                                                                colorScheme="blue"
                                                                variant="outline"
                                                                leftIcon={<Icon as={MdRoute} />}
                                                                onClick={() => onStartMapping(road)}
                                                                isDisabled={!!roadBeingMapped}
                                                                borderRadius="md"
                                                                _hover={{
                                                                    transform: 'translateY(-1px)',
                                                                    boxShadow: '0 4px 12px var(--chakra-colors-blue-200)',
                                                                }}
                                                                transition="all 0.2s"
                                                            >
                                                                Draw on Map
                                                            </Button>
                                                        )}
                                                    </Td>
                                                </MotionTr>
                                            );
                                        })}
                                    </AnimatePresence>
                                )}
                            </Tbody>
                        </Table>
                    </Box>

                    {/* Server-Side Pagination Controls */}
                    {totalPages > 1 && (
                        <Flex
                            justify="space-between"
                            align="center"
                            p={3}
                            borderTopWidth="1px"
                            borderColor={searchBorderColor}
                        >
                            <Text fontSize="xs" fontWeight="600" color={mutedColor}>
                                Showing page {page} of {totalPages}
                            </Text>
                            <HStack>
                                <IconButton
                                    icon={<Icon as={MdArrowBack} />}
                                    size="sm"
                                    variant="outline"
                                    isDisabled={page === 1 || isFetching}
                                    onClick={() => setPage(p => p - 1)}
                                    borderRadius="lg"
                                />
                                <IconButton
                                    icon={<Icon as={MdArrowForward} />}
                                    size="sm"
                                    variant="outline"
                                    isDisabled={page === totalPages || isFetching}
                                    onClick={() => setPage(p => p + 1)}
                                    borderRadius="lg"
                                />
                            </HStack>
                        </Flex>
                    )}
                </Box>
            </MotionBox>
        </VStack>
    );
};

export default memo(RoadMapDrawingPanel);